/**
 * IXVerify v2 — CheckReview (ixv2 WP-4.1; plan §3.3).
 *
 * The verifier's READ-ONLY evidence review, as a normal AppBar page (R7 —
 * the dispense wizard is the module's only full-screen surface):
 *   - both evidence surfaces side by side (assignment document · kit labels),
 *     presigned inline; PDFs/pasted text open in a new tab;
 *   - the assigned-vs-scanned kit table with per-kit results;
 *   - the DOB attestation status (subject decrypted server-side, audited);
 *   - the amber overrides/provenance box;
 *   - two actions when the viewer IS the assigned verifier on a pending
 *     check: **Approve & sign** (Part-11 modal: the exact signature meaning +
 *     password; the server re-authenticates via GoTrue and AUDITS failed
 *     attempts) and **Reject** (reason required).
 *
 * Deep links land here as /ixverify/checks/:id where :id may be a check id
 * OR a session id (the server resolves both — checks are UNIQUE per session).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  PenLine,
  ShieldCheck,
  UserCheck,
  XCircle,
} from 'lucide-react';
import { PageHeader, Modal, useToast, primaryBtnCls, secondaryBtnCls, fieldCls } from '../../ui';
import {
  getCheck,
  approveCheck,
  rejectCheck,
  errorMessage,
  isReauthFailed,
  isSecondCheckDisabled,
  isKillSwitched,
  CheckReviewPayload,
  CheckEvidenceItem,
  IxvKit,
} from '../lib/api';
import { SessionStatusPill } from '../components/shared/badges';

const pillBase =
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium';

function CheckStatusPill({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string; Icon: typeof Clock }> = {
    requested: { label: 'Awaiting verification', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', Icon: Clock },
    approved: { label: 'Countersigned', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300', Icon: CheckCircle2 },
    rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300', Icon: XCircle },
    expired: { label: 'Expired', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', Icon: Clock },
  };
  const { label, cls, Icon } = map[status] || map.expired;
  return (
    <span className={`${pillBase} ${cls}`}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  );
}

function KitResultPill({ kit }: { kit: IxvKit }) {
  const okStatuses = new Set(['matched', 'manual_confirmed']);
  const cls = okStatuses.has(kit.match_status)
    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
    : kit.match_status === 'pending'
    ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
  return (
    <span className={`${pillBase} ${cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {kit.match_status.replace('_', ' ')}
    </span>
  );
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function EvidenceTile({ item, alt }: { item: CheckEvidenceItem; alt: string }) {
  const isImage = (item.content_type || '').startsWith('image/');
  if (isImage && item.url) {
    return (
      <figure className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40">
        <img src={item.url} alt={alt} className="max-h-96 w-full object-contain" />
        <figcaption className="border-t border-gray-200 px-2.5 py-1.5 text-[10px] uppercase tracking-widest text-gray-400 dark:border-gray-700 dark:text-gray-500">
          SHA-256 {item.sha256 ? item.sha256.slice(0, 12) : 'n/a'}
        </figcaption>
      </figure>
    );
  }
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-900/40">
      <span className="flex min-w-0 items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
        <FileText className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
        <span className="truncate">
          {alt} <span className="text-xs text-gray-400">· SHA-256 {item.sha256 ? item.sha256.slice(0, 12) : 'n/a'}</span>
        </span>
      </span>
      {item.url ? (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
        >
          Open
        </a>
      ) : (
        <span className="text-xs text-gray-400">unavailable</span>
      )}
    </div>
  );
}

export default function CheckReview() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();

  const [payload, setPayload] = useState<CheckReviewPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<'not_found' | 'disabled' | 'dark' | 'error' | null>(null);

  // Approve & sign modal.
  const [signOpen, setSignOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [signError, setSignError] = useState<string | null>(null);
  const [signBusy, setSignBusy] = useState(false);

  // Reject modal.
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [rejectBusy, setRejectBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      setPayload(await getCheck(id));
    } catch (err) {
      if ((err as any)?.response?.status === 404) setLoadError('not_found');
      else if (isSecondCheckDisabled(err)) setLoadError('disabled');
      else if (isKillSwitched(err)) setLoadError('dark');
      else setLoadError('error');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const submitApprove = async () => {
    if (!payload) return;
    setSignBusy(true);
    setSignError(null);
    try {
      await approveCheck(payload.check.id, password, payload.countersign_meaning);
      setSignOpen(false);
      setPassword('');
      toast('Countersigned — the verified report is being redistributed');
      await load();
    } catch (err) {
      if (isReauthFailed(err)) {
        setSignError('Password incorrect — the attempt has been recorded.');
      } else {
        setSignError(errorMessage(err, 'Could not sign the verification'));
      }
    } finally {
      setSignBusy(false);
    }
  };

  const submitReject = async () => {
    if (!payload) return;
    if (!reason.trim()) {
      setRejectError('A reason is required.');
      return;
    }
    setRejectBusy(true);
    setRejectError(null);
    try {
      await rejectCheck(payload.check.id, reason.trim());
      setRejectOpen(false);
      setReason('');
      toast('Second check rejected — the creator has been notified');
      await load();
    } catch (err) {
      setRejectError(errorMessage(err, 'Could not reject the second check'));
    } finally {
      setRejectBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 motion-reduce:animate-none dark:text-primary-400" aria-hidden="true" />
      </div>
    );
  }

  if (loadError || !payload) {
    const copy: Record<string, { title: string; hint: string }> = {
      not_found: { title: 'Second check not found', hint: 'It may belong to another site, or you are not a participant on it.' },
      disabled: { title: 'Second-person verification is disabled', hint: 'This deployment currently has the feature switched off.' },
      dark: { title: 'IXVerify is currently disabled', hint: 'Please try again later.' },
      error: { title: 'Could not load the second check', hint: 'Please try again.' },
    };
    const c = copy[loadError || 'error'];
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
        <UserCheck className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" aria-hidden="true" />
        <h2 className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">{c.title}</h2>
        <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">{c.hint}</p>
        <Link
          to="/ixverify"
          className="mt-4 text-sm font-medium text-primary-700 hover:text-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
        >
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const { check, session, kits, kit_state: kitState, subject, evidence, viewer } = payload;
  const overrides = session.overrides || [];
  const manualKits = kits.filter((k) => k.scan_method === 'manual' || k.match_status === 'manual_confirmed');
  const claims = session.assignment?.attestation?.claims || [];

  return (
    <div className="space-y-4">
      <PageHeader
        icon={UserCheck}
        title="Second check"
        subtitle={session.verification_code || undefined}
        actions={
          <div className="flex items-center gap-2">
            <CheckStatusPill status={check.status} />
            <SessionStatusPill status={session.status} />
          </div>
        }
      />

      {/* Status banner */}
      {check.status === 'requested' && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          {viewer.is_verifier
            ? `${check.requester?.name || 'A colleague'} asked you to verify this dispense. Review the evidence below, then approve & sign or reject.`
            : `Awaiting verification by ${check.verifier?.name || 'the assigned verifier'}.`}
          {check.expires_at && ` The request expires ${fmtDateTime(check.expires_at)}.`}
        </p>
      )}
      {check.status === 'approved' && (
        <p className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
          Countersigned by {check.verifier?.name || 'the verifier'} at {fmtDateTime(check.decided_at)}. The verified report
          (revision 2) is distributed to the report recipients.
        </p>
      )}
      {check.status === 'rejected' && (
        <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400" role="alert">
          Rejected by {check.verifier?.name || 'the verifier'} at {fmtDateTime(check.decided_at)} — flagged for admin review.
          {check.rejection_reason && ` Reason: ${check.rejection_reason}`}
        </p>
      )}
      {check.status === 'expired' && (
        <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
          This request expired before a decision. The creator can re-request a second check from History.
        </p>
      )}

      {/* Evidence — side by side (§3.3) */}
      <section aria-label="Evidence" className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">IXRS assignment</h2>
          {evidence.documents.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No assignment evidence on this session.</p>
          ) : (
            <div className="space-y-3">
              {evidence.documents.map((d, i) => (
                <EvidenceTile key={d.id} item={d} alt={`Assignment ${d.kind === 'ixrs_text' ? 'pasted text' : d.kind === 'ixrs_document' ? 'document (PDF)' : 'photo'} ${i + 1}`} />
              ))}
            </div>
          )}
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Kit labels</h2>
          {evidence.labels.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No label photos (barcode/manual scans only).</p>
          ) : (
            <div className="space-y-3">
              {evidence.labels.map((l, i) => (
                <EvidenceTile key={l.id} item={l} alt={`Kit label photo ${i + 1}`} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Assigned vs scanned */}
      <section aria-label="Kits" className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Assigned vs scanned</h2>
          <span className={`${pillBase} ${kitState.pass ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'}`}>
            {kitState.pass ? <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> : <AlertTriangle className="h-3 w-3" aria-hidden="true" />}
            {kitState.pass ? 'All kits match' : 'Discrepancies present'}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                {['Assigned', 'Scanned', 'Method', 'Result'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {kits.map((k) => (
                <tr key={k.id}>
                  <td className="px-4 py-3 text-sm tabular-nums text-gray-900 dark:text-gray-100">{k.assigned_kit_number || '—'}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-gray-900 dark:text-gray-100">{k.scanned_kit_number || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{k.scan_method || '—'}</td>
                  <td className="px-4 py-3"><KitResultPill kit={k} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Subject + DOB attestation (the decrypt is server-side and audited) */}
      <section aria-label="Subject" className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Subject &amp; DOB attestation</h2>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-[10px] font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">Subject</dt>
            <dd className="tabular-nums text-gray-900 dark:text-gray-100">{subject?.subject_number || '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">Initials</dt>
            <dd className="text-gray-900 dark:text-gray-100">{subject?.initials || '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">Date of birth</dt>
            <dd className="font-medium tabular-nums text-gray-900 dark:text-gray-100">{subject?.dob || '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">Visit</dt>
            <dd className="text-gray-900 dark:text-gray-100">{session.visit_name || '—'}</dd>
          </div>
        </dl>
        {session.dob_attested_at ? (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400">
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            DOB verbally confirmed with the patient at {fmtDateTime(session.dob_attested_at)}
          </p>
        ) : (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-red-700 dark:text-red-400" role="alert">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            DOB attestation not recorded
          </p>
        )}
        {claims.length > 0 && (
          <ul className="mt-3 space-y-1 border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
            {claims.map((c) => (
              <li key={c} className="flex items-start gap-1.5">
                <PenLine className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
                <span>Creator attested: {c}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Provenance — manual entries & overrides (amber box, §3.6 pattern) */}
      {(overrides.length > 0 || manualKits.length > 0) && (
        <section
          aria-label="Manual entries and overrides"
          className="rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20"
        >
          <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-amber-800 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            Manual entries &amp; overrides
          </h2>
          <ul className="space-y-1 text-sm text-amber-800 dark:text-amber-300">
            {overrides.map((o, i) => (
              <li key={`o-${i}`}>• {o.field}: {o.reason || 'no reason recorded'}</li>
            ))}
            {manualKits.map((k) => (
              <li key={`k-${k.id}`}>• kit {k.scanned_kit_number || k.assigned_kit_number}: {k.manual_reason || 'manual decision recorded'}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Actions — the assigned verifier on a pending, unexpired check only */}
      {viewer.can_decide && (
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => { setRejectOpen(true); setRejectError(null); }}
            className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 sm:min-h-0"
            data-testid="reject-check"
          >
            <XCircle className="h-4 w-4" aria-hidden="true" />
            Reject
          </button>
          <button
            type="button"
            onClick={() => { setSignOpen(true); setSignError(null); setPassword(''); }}
            className={primaryBtnCls}
            data-testid="approve-check"
          >
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            Approve &amp; sign
          </button>
        </div>
      )}

      {/* Approve & sign — the Part-11 re-auth modal */}
      <Modal isOpen={signOpen} onClose={() => !signBusy && setSignOpen(false)} title="Approve & sign">
        <div className="space-y-4 p-4">
          <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
            &ldquo;{payload.countersign_meaning}&rdquo;
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Signing requires re-entering your password (21 CFR Part 11). Failed attempts are recorded in the audit trail.
          </p>
          <div>
            <label htmlFor="ixv-sign-password" className="mb-1 block text-xs font-medium uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Password
            </label>
            <input
              id="ixv-sign-password"
              type="password"
              autoComplete="current-password"
              className={`${fieldCls} w-full`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && password && !signBusy) void submitApprove(); }}
            />
          </div>
          {signError && (
            <p className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400" role="alert">
              {signError}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 p-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={() => setSignOpen(false)} disabled={signBusy}>
            Cancel
          </button>
          <button
            type="button"
            className={primaryBtnCls}
            onClick={() => void submitApprove()}
            disabled={signBusy || !password}
            data-testid="submit-approve"
          >
            {signBusy && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            Sign verification
          </button>
        </div>
      </Modal>

      {/* Reject modal — reason required */}
      <Modal isOpen={rejectOpen} onClose={() => !rejectBusy && setRejectOpen(false)} title="Reject second check">
        <div className="space-y-4 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            The creator will be notified and the session flagged for admin review. A reason is required.
          </p>
          <div>
            <label htmlFor="ixv-reject-reason" className="mb-1 block text-xs font-medium uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Reason
            </label>
            <textarea
              id="ixv-reject-reason"
              rows={3}
              className={`${fieldCls} w-full`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          {rejectError && (
            <p className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400" role="alert">
              {rejectError}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 p-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={() => setRejectOpen(false)} disabled={rejectBusy}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submitReject()}
            disabled={rejectBusy || !reason.trim()}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
            data-testid="submit-reject"
          >
            {rejectBusy && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            Reject
          </button>
        </div>
      </Modal>
    </div>
  );
}
